import React, { ChangeEvent, FC, useState } from "react";
import Input from "react-phone-number-input/input";
import styles from "./Input.module.css";
// import tick from "../tick.svg";
import { CustomerContact } from "../../../types";
import { ICaret, ICheck } from "../../Icons";

interface InputProps {
  toggle: {
    current: boolean;
    set: React.Dispatch<React.SetStateAction<boolean>>;
  };
  onChange: (_contactNumber: string) => void;
  value: CustomerContact;
}

const maxLength = 10;

const InputField: FC<InputProps> = ({ toggle, value, onChange }) => {
  const [contactNumber, setContactNumber] = useState<string>(value.number);
  const [focus, setFocus] = useState<boolean>(false);
  const setContactNumberValue = (contact: string) => {
    setContactNumber(contact);
    onChange(contact);
  };

  const getNumberWithoutDialingCode = (contactNumber: string, dialingCode: string): string => {
    if (!contactNumber) return "";
    return contactNumber.substring(dialingCode.length + 1);
  };

  const contactWithNoDialCode = getNumberWithoutDialingCode(value.number, value.dialingCode);

  return (
    <>
      <div
        className={styles.Input}
        style={focus ? { borderColor: "#FFCD00" } : {}}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      >
        <div className={styles.Row}>
          <div className={styles.Flag}>{value.image && value.image({ title: value.country })}</div>
          <div className={styles.Code}>+{value.dialingCode}</div>
          <div
            className={styles.Divider}
            onClick={() => toggle.set(!toggle.current)}
            role="button"
            onKeyDown={() => {}}
            tabIndex={0}
          >
            <ICaret color="#1d1d1b" className={toggle.current ? styles.Up : styles.Down} />
          </div>
          <div className={styles.Value}>
            <Input
              country={value.ISO}
              international
              // eslint-disable-next-line no-use-before-define
              inputComponent={CustomInput as any}
              value={value.number}
              onChange={(phoneNumber) => setContactNumberValue(phoneNumber ?? "")}
              maxLength={maxLength}
            />
          </div>
          <div className={styles.Check}>
            {(contactNumber ?? 0).length - value.dialingCode.length > 3 && <ICheck width="24px" height="24px" />}
          </div>
        </div>
      </div>
      <div className={styles.MaxCharacters}>
        <div>Max characters</div>
        <div>{maxLength - contactWithNoDialCode.length}</div>
      </div>
    </>
  );
};

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  // eslint-disable-next-line react/require-default-props
  maxLength?: number;
}

/**
 * Required because phone number library introduces spaces in the number:
 * we need to override the value and control max length manually */
const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>((props, ref) => {
  const { maxLength, ...rest } = props;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const newValueNoSpaces = newValue.replace(/\s/g, "");

    // don't allow leading zeros
    if (newValue === "0") {
      // eslint-disable-next-line no-param-reassign
      e.target.value = "";
    }

    if (maxLength && newValueNoSpaces.length > maxLength) {
      return;
    }

    // Call the original onChange handler provided by PhoneInput
    if (props.onChange) {
      (props.onChange as React.ChangeEventHandler<HTMLInputElement>)(e);
    }
  };

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <input {...rest} ref={ref} onChange={handleChange} />;
});

export default InputField;
